import { generateVariantAttributeDictionary } from '@modules/cart/components/CartItemList/hooks/useLineItemAttributes'
import { formatMuralSizeDisplay } from '@modules/cart/helpers/mural'
import { buildHashTable } from '@modules/cart/utils'
import { CartFragment, LineItem, RawProductAttribute } from '../../types'
import { Discount } from '../../utils/checkout/helpers/types'
import { ExtendedGaItemParams } from '../gaTracking'

export const MURAL_CATEGORY = 'Wall Mural'
type InternalGaItemFields = ExtendedGaItemParams & { mural_group_id?: string }

type gaItemRequiredFields = { item_id: string; item_name: string }
export type gaItemFields = Partial<ExtendedGaItemParams> & gaItemRequiredFields

export const formatPrice = (price: number = 0) => {
  return price / 100
}

// Mural ID is a UUID, but we use the base32 representation of the UUID for GA tracking.
const BASE_32_SYMBOL_SET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

export function uuidToBase32(uuid: string) {
  const uuidHex = uuid.replaceAll('-', '')
  if (!/^[0-9a-fA-F]{32}$/.test(uuidHex)) {
    return uuid
  }

  const bigint = BigInt(`0x${uuidHex}`)

  return [...Array(26).keys()]
    .map((i) => {
      const index = Number((bigint >> (BigInt(i) * BigInt(5))) & BigInt(31))
      return BASE_32_SYMBOL_SET[index]
    })
    .reverse()
    .join('')
}

export const filterGaItems = (items: ExtendedGaItemParams[]) =>
  items.map((obj) => {
    const filteredObj: gaItemFields = Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => {
        return value !== ''
      })
    ) as gaItemFields
    return filteredObj
  })

/**
 * Returns locale from the RawProductAttribute array
 * @param attributesRaw
 */
export const getLocaleFromRawProductAttribute = (
  attributesRaw: RawProductAttribute[]
) =>
  attributesRaw
    ? attributesRaw.reduce((acc, attribute) => {
        const { attributeDefinition, value } = attribute
        const { type } = attributeDefinition ?? {}
        const { name: attributeType } = type ?? {}
        const isLocalizedObject =
          typeof value === 'object' && value !== null && !Array.isArray(value)

        if (
          (attributeType === 'lenum' || attributeType === 'ltext') &&
          isLocalizedObject
        ) {
          return Object.keys(value)?.[0] || acc
        }
        return acc
      }, 'en-US')
    : 'en-US'

/**
 * Returns {discount, coupon} object with discount, applied to the lineItem
 * @param lineItem
 */
const getItemTotal = (lineItem: LineItem) => {
  const discountedPriceTotals = lineItem?.discountedPricePerQuantity?.reduce(
    (acc, discount) => {
      const discountCoupon = discount.discountedPrice.includedDiscounts.reduce(
        (prev, discountCode) => (prev += discountCode?.discount?.name + ' '),
        ''
      )
      const discountAmount = discount.discountedPrice.includedDiscounts.reduce(
        (prev, discountCode) =>
          (prev += discountCode?.discountedAmount?.centAmount),
        0
      )

      return {
        coupon: `${acc.coupon}${discountCoupon}`,
        discount: acc.discount + discountAmount,
      }
    },
    {
      discount: 0,
      coupon: '',
    }
  ) ?? {
    discount: 0,
    coupon: '',
  }

  if (discountedPriceTotals.discount > 0) {
    return discountedPriceTotals
  }

  // product level discounts are not showing in lineItem.discountedPricePerQuantity
  // like cart discounts. So we'll check the price for discounted value and then
  // calculate the discount from the list price and discounted price

  const listPrice = lineItem?.price?.value?.centAmount ?? 0
  const discountedListPrice = lineItem?.price?.discounted?.value?.centAmount

  if (
    typeof discountedListPrice === 'number' &&
    discountedListPrice >= 0 &&
    listPrice > discountedListPrice
  ) {
    const coupon = lineItem?.price?.discounted?.discount?.name

    return {
      discount: listPrice - discountedListPrice,
      coupon: typeof coupon === 'string' ? coupon : '',
    }
  }

  return discountedPriceTotals
}

/**
 * get the items that have values from the cart
 * @param lineItems
 */

export const getItemsWithValues = (
  lineItems?: Partial<CartFragment['lineItems']>
): gaItemFields[] => {
  const items: InternalGaItemFields[] =
    lineItems?.map((lineItem, index) => {
      const customFields = buildHashTable(lineItem?.custom?.customFieldsRaw)
      const locale = getLocaleFromRawProductAttribute(
        lineItem?.variant?.attributesRaw as RawProductAttribute[]
      )
      const attributeFields = generateVariantAttributeDictionary(
        lineItem?.variant?.attributesRaw as RawProductAttribute[],
        locale
      )

      const { coupon, discount } = getItemTotal(lineItem as LineItem)

      const price = discount
        ? lineItem?.price?.value?.centAmount - discount
        : lineItem?.price?.value?.centAmount

      const { key: productTypeKey } = lineItem?.productType ?? {}
      const excludedLineItemNames = [
        'Living And Decor',
        'Bedding',
        'Dining',
        'Printed Fabric',
      ]
      const isExcluded = excludedLineItemNames.some((excludedName) =>
        lineItem?.name?.toLowerCase().includes(excludedName.toLowerCase())
      )
      const isMigrated =
        !isExcluded &&
        (productTypeKey === 'wallpaper' ||
          (productTypeKey === 'fabric' &&
            !lineItem?.name?.toLowerCase().includes('printed fabric')))
      const muralId = customFields?.muralId ?? ''
      const isMural = Boolean(muralId)
      const productFormatValue =
        attributeFields?.productFormat?.value ?? attributeFields?.format?.value
      const isMuralSample =
        isMural &&
        String(productFormatValue ?? '')
          .toLowerCase()
          .trim() === 'sample'

      const fullyQualifiedSku = `${lineItem?.variant?.sku}${customFields?.designID ? `-D${customFields.designID}` : ''}`

      return {
        currency: lineItem?.totalPrice?.currencyCode ?? '',
        design_id: customFields?.designID ?? '',
        mural_group_id: isMuralSample ? undefined : muralId,
        item_brand: customFields?.artistName ?? '',
        item_category: isMural
          ? MURAL_CATEGORY
          : (lineItem?.productType?.name ?? ''),
        item_id: lineItem?.variant?.sku ? fullyQualifiedSku : '',
        item_name: isMigrated
          ? lineItem?.name
          : (attributeFields?.class?.value ?? ''),
        item_parent_id: customFields?.parentSku ?? '',
        price: formatPrice(price ?? 0),
        quantity: lineItem?.quantity ?? 0,
        item_variant: attributeFields?.['size-display']?.value,
        discount: formatPrice(discount),
        coupon,
        index,
      }
    }) ?? []

  const groupedItems = items.reduce<InternalGaItemFields[]>((acc, item) => {
    if (!item?.mural_group_id) {
      acc.push(item)
      return acc
    }

    const itemQuantity = item.quantity ?? 0
    const itemTotalPrice = (item.price ?? 0) * itemQuantity
    const itemTotalDiscount = (item.discount ?? 0) * itemQuantity

    // group by muralId and item_name (substrate)
    const existing = acc.find(
      (candidate) =>
        candidate.mural_group_id === item.mural_group_id &&
        candidate.item_name === item.item_name
    )

    if (!existing) {
      item.price = itemTotalPrice
      item.discount = itemTotalDiscount
      acc.push(item)
      return acc
    }

    const existingQuantity = existing.quantity ?? 0
    const totalQuantity = existingQuantity + itemQuantity

    existing.quantity = totalQuantity
    existing.price = (existing.price ?? 0) + itemTotalPrice
    existing.discount = (existing.discount ?? 0) + itemTotalDiscount

    const mergedCoupons = Array.from(
      new Set(
        [existing.coupon, item.coupon]
          .map((c) => (c ?? '').trim())
          .filter(Boolean)
      )
    ).join(' ')

    existing.coupon = mergedCoupons
    existing.index = Math.min(
      existing.index ?? Number.MAX_SAFE_INTEGER,
      item.index ?? Number.MAX_SAFE_INTEGER
    )

    return acc
  }, [])

  const normalizedItems = groupedItems.map((item) => {
    if (!item?.mural_group_id) {
      return item
    }

    const muralRollCount = item.quantity ?? 0
    const itemVariantTemplate =
      typeof item.item_variant === 'string' ? item.item_variant : undefined

    item.item_variant =
      formatMuralSizeDisplay(itemVariantTemplate, muralRollCount) ??
      item.item_variant
    // update quantity to 1 now that we've set the roll count in the item_variant
    item.quantity = 1
    // update design_id to be base32 representation of mural id for GA tracking
    item.design_id = uuidToBase32(item.mural_group_id)
    // update the item_id to use the mural id
    item.item_id =
      item.item_id?.replace(/-D.+$/, `-D${item.design_id}`) ?? item.item_id

    return item
  })

  const dataLayerItems = normalizedItems.map((item) => {
    const { mural_group_id: _, ...publicItem } = item
    return publicItem
  })

  const reindexedItems = dataLayerItems.map((item, index) => ({
    ...item,
    index,
  }))

  return filterGaItems(reindexedItems)
}

export const concatDiscountCodesWithSpace = (discountCodes: Discount[]) => {
  const discountCodeNames = Array.from(
    new Set(discountCodes.map((discount) => discount.name))
  )
  return discountCodeNames.join(', ')
}
